import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BarChart from '../../../components/BarChart/BarChart';
import NoData from '../../../components/NoData/NoData';
import CommissionContractsStatisticsAPI, {
  type PipelineStage,
  type PipelineStagesResponse,
} from '../../../api/CommissionContractsStatisticsAPI';
import { translateCommissionStatus } from '../../../shared/commissionStatusToTranslationKey';

type Props = { timeRange: { start: string; end: string } };

function formatDays(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return value.toFixed(1);
}

function CommissionContractsPipelineStages({ timeRange }: Props) {
  const { t } = useTranslation();
  const [data, setData] = useState<PipelineStagesResponse | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const res = await CommissionContractsStatisticsAPI.getPipelineStages({
          startTime: timeRange.start,
          endTime: timeRange.end,
        });
        if (!mounted) return;
        setData(res.data ?? null);
      } catch (error) {
        console.error('Failed to load commission contracts pipeline stages', {
          startTime: timeRange.start,
          endTime: timeRange.end,
          error,
        });
        if (!mounted) return;
        setData(null);
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [timeRange.start, timeRange.end]);

  const chartConfig = useMemo(() => {
    if (!data) return null;

    const allStages: PipelineStage[] = [...(data.stages ?? []), ...(data.terminalStages ?? [])];
    const hasAnyReached = allStages.some((stage) => stage.countReached > 0);
    if (!hasAnyReached) return null;

    const labels = allStages.map((stage) => translateCommissionStatus(stage.status, t));
    const reachedCounts = allStages.map((stage) => stage.countReached);
    const avgDays = allStages.map((stage) => stage.averageDays ?? 0);

    const reachedBarColor = 'rgba(59, 130, 246, 0.75)';
    const reachedBorderColor = 'rgba(59, 130, 246, 1)';
    const avgDaysBarColor = 'rgba(16, 185, 129, 0.75)';
    const avgDaysBorderColor = 'rgba(16, 185, 129, 1)';

    return {
      labels,
      dataSets: [
        {
          label: t('stats.commission_contracts.pipeline_count_reached'),
          data: reachedCounts,
          backgroundColor: reachedCounts.map(() => reachedBarColor),
          borderColor: reachedCounts.map(() => reachedBorderColor),
          borderWidth: 1,
        },
        {
          label: t('stats.commission_contracts.pipeline_avg_days'),
          data: avgDays,
          backgroundColor: avgDays.map(() => avgDaysBarColor),
          borderColor: avgDays.map(() => avgDaysBorderColor),
          borderWidth: 1,
        },
      ],
      allStages,
    };
  }, [data, t]);

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {t('stats.commission_contracts.pipeline_stages_recording_from')}
      </p>
      {!chartConfig ? (
        <NoData message={t('global.empty_results')} />
      ) : (
        <>
          <BarChart
            title=""
            xLabels={chartConfig.labels}
            dataSets={chartConfig.dataSets}
            horizontal
            height={Math.max(280, chartConfig.labels.length * 48)}
          />
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
            {chartConfig.allStages.map((stage) => (
              <li key={stage.status}>
                <span className="font-medium text-foreground">
                  {translateCommissionStatus(stage.status, t)}
                </span>
                {': '}
                {t('stats.commission_contracts.pipeline_stage_summary', {
                  reached: stage.countReached,
                  avgDays: formatDays(stage.averageDays),
                  sampleSize: stage.sampleSize,
                })}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default CommissionContractsPipelineStages;
